/** @format */

import React, { memo } from "react";
import { EdgeProps } from "react-flow-renderer";
import { TOrgEdgeData } from "../utils/layout";

type TPoint = [number, number];

// Colori come attributi SVG (non solo CSS): html-to-image clona gli <svg> senza
// copiare gli stili calcolati dei figli, quindi l'export li perderebbe.
const LINE_COLOR = "#a0aab8";
const STAFF_COLOR = "#d9a441";

/** Spezzata ortogonale con angoli arrotondati. */
const roundedPath = (points: TPoint[], radius: number): string => {
  let d = `M ${points[0][0]},${points[0][1]}`;

  for (let i = 1; i < points.length - 1; i++) {
    const [px, py] = points[i - 1];
    const [cx, cy] = points[i];
    const [nx, ny] = points[i + 1];
    const lenPrev = Math.hypot(cx - px, cy - py);
    const lenNext = Math.hypot(nx - cx, ny - cy);
    const r = Math.min(radius, lenPrev / 2, lenNext / 2);

    if (r < 0.5) {
      d += ` L ${cx},${cy}`;
      continue;
    }

    const sx = cx - ((cx - px) / lenPrev) * r;
    const sy = cy - ((cy - py) / lenPrev) * r;
    const ex = cx + ((nx - cx) / lenNext) * r;
    const ey = cy + ((ny - cy) / lenNext) * r;
    d += ` L ${sx},${sy} Q ${cx},${cy} ${ex},${ey}`;
  }

  const last = points[points.length - 1];
  return `${d} L ${last[0]},${last[1]}`;
};

/**
 * Collegamento dell'organigramma.
 * - linea gerarchica: scende dal responsabile, piega a bendOffset e scende sul collaboratore
 * - staff (tratteggiato): scende dal responsabile e gira a sinistra nel lato destro del nodo staff
 */
const OrgEdgeComponent = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  data,
}: EdgeProps<TOrgEdgeData>): JSX.Element => {
  const isStaff = Boolean(data?.isStaff);
  let points: TPoint[];

  if (isStaff) {
    points = [
      [sourceX, sourceY],
      [sourceX, targetY],
      [targetX, targetY],
    ];
  } else if (Math.abs(sourceX - targetX) < 1) {
    points = [
      [sourceX, sourceY],
      [targetX, targetY],
    ];
  } else {
    const bendY = sourceY + (data?.bendOffset ?? 45);
    points = [
      [sourceX, sourceY],
      [sourceX, bendY],
      [targetX, bendY],
      [targetX, targetY],
    ];
  }

  const className = [
    "react-flow__edge-path",
    "org-edge",
    isStaff && "org-edge--staff",
    data?.dimmed && "org-edge--dim",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <path
      id={id}
      className={className}
      d={roundedPath(points, 12)}
      fill="none"
      stroke={isStaff ? STAFF_COLOR : LINE_COLOR}
      strokeWidth={1.6}
      strokeDasharray={isStaff ? "6 5" : undefined}
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={data?.dimmed ? 0.25 : 1}
    />
  );
};

export const OrgEdge = memo(OrgEdgeComponent);
OrgEdge.displayName = "OrgEdge";
